import {
  CompaniesInfiniteSelectField,
  FloatingFormItem,
  InputDate,
} from '@/components/_common';
import { useInvoiceAuxiliaryDocumentsAllPDF } from '@/core/domain/invoiceTransmissionHub/AuxiliaryDocuments/AuxiliaryDocuments.hooks';
import { useInvoiceNFSAllPDF } from '@/core/domain/nfs/NfseController/NfseController.hooks';
import { useDefaultEmptyFieldError, useReportGenerate } from '@/hooks';
import { Alert, Col, Form, Modal, Radio, Row } from 'antd';
import { Moment } from 'moment';
import { useCallback } from 'react';

interface Props {
  opened: boolean;
  onClose: VoidFunction;
}

interface FormValues {
  invoiceType: 'NFS' | 'NF-NFC';
  companyId: number;
  startDate: Moment;
  endDate: Moment;
}

export default function PDFDownloadModal(props: Props) {
  const [form] = Form.useForm<FormValues>();
  const startDate = Form.useWatch('startDate', form);
  const endDate = Form.useWatch('endDate', form);

  const required = useDefaultEmptyFieldError();

  const dowloadNFSAllPDF = useInvoiceNFSAllPDF();
  const dowloadNFAllPDF = useInvoiceAuxiliaryDocumentsAllPDF();

  const isLoading = dowloadNFSAllPDF.isLoading || dowloadNFAllPDF.isLoading;

  const report = useReportGenerate();

  const handleClose = () => {
    if (isLoading) return;

    form.resetFields();
    props.onClose();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const params = {
        companyId: values.companyId,
        startDate: values.startDate.format('YYYY-MM-DD'),
        endDate: values.endDate.format('YYYY-MM-DD'),
      };

      let result;

      if (values.invoiceType === 'NFS') {
        result = await dowloadNFSAllPDF.mutateAsync(params);
      } else if (values.invoiceType === 'NF-NFC') {
        result = await dowloadNFAllPDF.mutateAsync(params);
      }

      if (result) {
        report.downloadFile({
          fileName: `invoice-${values.invoiceType}-pdfs_${params.startDate}_${params.endDate}`,
          result,
          type: 'application/zip',
        });
      }

      handleClose();
    } catch {}
  };

  const disabledStartDate = useCallback(
    (date: Moment) => {
      if (!endDate) return false;

      const maxStartDate = endDate.clone().subtract(2, 'month');

      return date.isAfter(endDate, 'day') || date.isBefore(maxStartDate, 'day');
    },
    [endDate],
  );

  const disabledEndDate = useCallback(
    (date: Moment) => {
      if (!startDate) return false;

      const maxEndDate = startDate.clone().add(2, 'month');

      return date.isBefore(startDate, 'day') || date.isAfter(maxEndDate, 'day');
    },
    [startDate],
  );

  return (
    <Modal
      title="Baixar arquivos PDFs"
      open={props.opened}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText="Baixar PDFs"
      confirmLoading={isLoading}
      cancelButtonProps={{ disabled: isLoading }}
    >
      <Form form={form} layout="vertical">
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Form.Item
              name="invoiceType"
              label="Selecione o tipo"
              initialValue="NFS"
              rules={[required]}
            >
              <Radio.Group>
                <Radio value="NFS">NFS-e</Radio>
                <Radio value="NF-NFC">NF-e/NFC-e</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>

          <Col span={24}>
            <FloatingFormItem
              name="companyId"
              label="Empresa"
              rules={[required]}
            >
              <CompaniesInfiniteSelectField />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12}>
            <FloatingFormItem
              name="startDate"
              label="Data inicial"
              rules={[
                required,
                ({ getFieldValue }) => ({
                  validator(_, value: Moment) {
                    const endDateField = getFieldValue('endDate')?.clone();

                    if (
                      !!value &&
                      !!endDateField &&
                      value
                        .clone()
                        .isBefore(endDateField?.subtract(2, 'months'))
                    ) {
                      return Promise.reject(new Error('Limite de 2 meses'));
                    }
                    form.setFields([{ name: 'endDate', errors: [] }]);
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputDate allowClear disabledDate={disabledStartDate} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12}>
            <FloatingFormItem
              name="endDate"
              label="Data final"
              rules={[
                required,
                ({ getFieldValue }) => ({
                  validator(_, value: Moment) {
                    const startDateField = getFieldValue('startDate')?.clone();

                    if (
                      !!value &&
                      !!startDateField &&
                      value.clone().isAfter(startDateField?.add(2, 'months'))
                    ) {
                      return Promise.reject(new Error('Limite de 2 meses'));
                    }
                    form.setFields([{ name: 'startDate', errors: [] }]);
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputDate allowClear disabledDate={disabledEndDate} />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <Alert
              type="warning"
              description="Limite de período de seleção das datas de 2 meses."
            />
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
